import * as React from "react";
import {css} from "/src/utils/utils";
import TransitionBlock, {
  TRANSITION_BLOCK_FROM,
} from "../../../animation/transition-block/TransitionBlock";
import JTBDCollection from "../../../jtbd-collection/JTBDCollection";
import {SlideWithFeaturedCards} from "./slide-with-featured-cards/SlideWithFeaturedCards";
import {BasicSlide} from "./basic-slide/BasicSlide";
import {CarouselMedia} from "../carousel-media/CarouselMedia";
import * as styles from "./carousel-slide.module.scss";

function getSlideContentSizeStyle(
  slide:
    | Queries.CarouselSlideFragmentFragment
    | Queries.JtbdCollectionFragmentFragment,
  mediaSpaceAllocation: string | null
) {
  if (
    slide.__typename === "ContentfulJtbdCollection" ||
    (slide.__typename === "ContentfulCarouselSlide" && !!slide.featuredCards)
  ) {
    return styles.carouselSlide__content100;
  }

  return mediaSpaceAllocation === "75%"
    ? styles.carouselSlide__content25
    : styles.carouselSlide__content50;
}

enum MediaSpaceAllocationEnum {
  FIFTY_PERCENT = "50%",
  SEVENTY_FIVE_PERCENT = "75%",
}

enum TextTransitionEnum {
  FADE = "fade",
  NONE = "none",
}

enum ImageLocationEnum {
  RIGHT = "right",
  LEFT = "left",
}

interface Props
  extends Pick<
    Queries.CarouselFragmentFragment,
    "mediaSpaceAllocation" | "imageLocation" | "textTransition"
  > {
  selectedSlideIndex: number;
  slide:
    | Queries.CarouselSlideFragmentFragment
    | Queries.JtbdCollectionFragmentFragment;
  slideIndex: number;
  mobileLayoutOnly?: boolean;
  darkMode?: boolean;
}

export function CarouselSlide(props: Props) {
  const {
    slide,
    mediaSpaceAllocation = MediaSpaceAllocationEnum.FIFTY_PERCENT,
    textTransition = TextTransitionEnum.FADE,
    imageLocation = ImageLocationEnum.RIGHT,
    mobileLayoutOnly,
    slideIndex,
    selectedSlideIndex,
    darkMode = false,
  } = props;
  if (!slide) {
    return null;
  }

  const isHidden =
    selectedSlideIndex === -1 ? false : selectedSlideIndex !== slideIndex;

  const isContentfulSlide = slide.__typename === "ContentfulCarouselSlide";
  const hasFeaturedCards = isContentfulSlide && !!slide.featuredCards;

  const contentSizeStyle = getSlideContentSizeStyle(
    slide,
    mediaSpaceAllocation
  );
  return (
    <div
      aria-hidden={isHidden}
      className={css(
        styles.carouselSlide,
        !mobileLayoutOnly ? styles.carouselSlideResponsive : "",
        isHidden ? styles.carouselSlideHidden : ""
      )}
    >
      <div className={css(styles.carouselSlide__content, contentSizeStyle)}>
        {textTransition !== TextTransitionEnum.NONE ? (
          <TransitionBlock
            key={slideIndex}
            from={TRANSITION_BLOCK_FROM.bottom}
            delay={25}
            transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
            threshold={0.01}
          >
            <Slide slide={slide} darkMode={darkMode} />
          </TransitionBlock>
        ) : (
          <Slide slide={slide} />
        )}
      </div>

      {isContentfulSlide && !hasFeaturedCards && (
        <div
          className={css(
            styles.carouselSlide__content,
            mediaSpaceAllocation ===
              MediaSpaceAllocationEnum.SEVENTY_FIVE_PERCENT
              ? styles.carouselSlide__content75
              : styles.carouselSlide__content50,
            styles.carouselSlide__media,
            !mobileLayoutOnly ? styles.carouselSlide__mediaResponsive : "",
            imageLocation === ImageLocationEnum.LEFT
              ? styles.carouselSlide__mediaLeft
              : ""
          )}
        >
          {isContentfulSlide && <CarouselMedia {...slide} />}
        </div>
      )}
    </div>
  );
}

interface SlideProps {
  slide:
    | Queries.CarouselSlideFragmentFragment
    | Queries.JtbdCollectionFragmentFragment;
  darkMode?: boolean;
}

function Slide(props: SlideProps) {
  const {darkMode, slide} = props;
  switch (slide.__typename) {
    case "ContentfulJtbdCollection":
      return <JTBDCollection layout={slide.layout} cards={slide.cards} />;
    case "ContentfulCarouselSlide": {
      return slide.featuredCards ? (
        <SlideWithFeaturedCards cards={slide.featuredCards} />
      ) : (
        <BasicSlide {...slide} darkMode={darkMode} />
      );
    }
    default:
      return null;
  }
}
